import {Add} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, {useMemo, useState} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import {formatIncidentDateAnswerIfBlumar} from 'src/modules/incident/incidentDateWeekday';
import {
  effectiveIncidentAnswerType,
  seedYesNoFromCheckbox,
} from 'src/modules/incident/incidentFormAnswerCoerce';
import {
  alternativeSelectValue,
  findMultipleChoiceTreeFullPath,
  getMultipleChoiceTreeDisplayLabel,
} from 'src/modules/shared/alternativeTreeDisplay';
import {isContainerNotApplicable} from 'src/modules/tarea/tareaParcialHelpers';
import RespuestaFormularioForm from 'src/view/modeloFormulario/form/RespuestaFormularioForm';
import CheckboxFormItem from 'src/view/shared/form/items/CheckboxFormItem';
import ImageModal from 'src/view/shared/modals/ImageModal';

function findAlternativeById(alternativas: any[] = [], targetId: any): any {
  if (!Array.isArray(alternativas) || targetId === undefined || targetId === null) return null;
  for (const alt of alternativas) {
    if (alt?.id === targetId) return alt;
    if (Array.isArray(alt?.children) && alt.children.length > 0) {
      const found = findAlternativeById(alt.children, targetId);
      if (found) return found;
    }
  }
  return null;
}

function getRespuestaTexto(pregunta: any, tenant?: any) {
  if (!pregunta?.tipoRespuesta) return '-';
  if (pregunta.allowNotApplicable && pregunta.notApplicable) return i18n('common.notApplicable');

  const tipoRespuesta = effectiveIncidentAnswerType(pregunta);
  const respuesta =
    tipoRespuesta === 'yesNo'
      ? pregunta.yesNo ?? seedYesNoFromCheckbox(pregunta)
      : pregunta[tipoRespuesta];
  let respuestaTexto: any = '';
  const formatRelationLabels = (value: any) => {
    if (Array.isArray(value)) {
      const labels = value.map((item) => item?.label).filter(Boolean);
      return labels.length ? labels.join(', ') : '-';
    }
    return value?.label || '-';
  };

  if (tipoRespuesta === 'signature') {
    const signatureValue = pregunta?.signatureRef?.publicUrl || pregunta?.signatureRef?.downloadUrl || pregunta?.signature;
    respuestaTexto = signatureValue ? '[Firma cargada]' : '-';
  } else if (tipoRespuesta === 'date') {
    respuestaTexto = respuesta
      ? formatIncidentDateAnswerIfBlumar(respuesta, 'DD-MM-YYYY', pregunta.keyPointer, tenant)
      : '-';
  } else if (tipoRespuesta === 'time') {
    if (!respuesta) {
      respuestaTexto = '-';
    } else if (typeof respuesta === 'string' && /^\d{2}:\d{2}$/.test(respuesta.trim())) {
      respuestaTexto = respuesta.trim();
    } else {
      const parsedTime = moment(respuesta);
      respuestaTexto = parsedTime.isValid() ? parsedTime.format('HH:mm') : '-';
    }
  } else if (['usuario', 'user', 'perfilEmpleado', 'articulo', 'project'].includes(tipoRespuesta)) {
    respuestaTexto = formatRelationLabels(respuesta);
  } else if (tipoRespuesta === 'multiCheckbox') {
    const alternativasSeleccionadas = (Array.isArray(respuesta) ? respuesta : [])
      .filter((alt) => alt?.value)
      .map((alt) => alt?.label);

    respuestaTexto = alternativasSeleccionadas.length > 0 ? alternativasSeleccionadas.join(', ') : '-';

    if (pregunta.multiCheckboxOthers) {
      const othersValue = (Array.isArray(respuesta) ? respuesta : []).find((a) => a?.id === 'others');
      if (othersValue && othersValue.value) {
        respuestaTexto += `: ${pregunta.multiCheckboxOthers}`;
      }
    }
  } else if (tipoRespuesta === 'multipleChoice') {
    const alternativas = pregunta.alternativas || [];
    if (respuesta === 'others') {
      respuestaTexto = 'Otro: ' + (pregunta.multipleChoiceOthers || '');
    } else {
      const treeLabel = getMultipleChoiceTreeDisplayLabel(
        alternativas,
        respuesta,
        pregunta,
        'dynamicTable',
        findMultipleChoiceTreeFullPath,
      );
      if (treeLabel) {
        respuestaTexto = treeLabel;
      } else {
        const alternativaSeleccionada = findAlternativeById(alternativas, respuesta);
        respuestaTexto = alternativaSeleccionada?.label || respuesta || '-';
      }
    }
  } else if (respuesta?.label) {
    respuestaTexto = respuesta.label;
  } else {
    respuestaTexto = respuesta || '-';
  }

  if (typeof respuestaTexto === 'string' && respuestaTexto.length > 35) {
    return `${respuestaTexto.slice(0, 35)}...`;
  }

  return String(respuestaTexto ?? '-');
}

function pickImagePreviewUrl(raw: any): string | undefined {
  if (!raw) return undefined;
  return raw.downloadUrl || raw.publicUrl || raw.thumbUri || raw.url || raw.uri || raw?.file?.uri;
}

/** Imágenes de respuesta mostradas en celdas de tabla dinámica (mismo origen que ImagesUploader). */
function getImageAttachmentsForDynamicCell(pregunta: any): any[] {
  if (!pregunta?.tipoRespuesta) return [];
  const t = pregunta.tipoRespuesta;
  if (t === 'images') {
    const arr = pregunta.images;
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  }
  if (t === 'files' && Array.isArray(pregunta.images)) {
    return pregunta.images.filter(Boolean);
  }
  if (t === 'textFromImage' && Array.isArray(pregunta.image)) {
    return pregunta.image.filter(Boolean);
  }
  if (t === 'signature') {
    const sig =
      (typeof pregunta.signature === 'string' ? pregunta.signature : null) ||
      pregunta?.signatureRef?.publicUrl ||
      pregunta?.signatureRef?.downloadUrl;
    if (typeof sig === 'string' && (sig.startsWith('http://') || sig.startsWith('https://') || sig.startsWith('data:'))) {
      return [{publicUrl: sig, downloadUrl: sig, name: 'Firma'}];
    }
  }
  return [];
}

function preguntaTieneAgregadosVisibles(pregunta: any): boolean {
  const hasCriticality = pregunta?.haveCriticality && pregunta?.criticality;
  const hasComentario =
    pregunta?.tieneComentario && pregunta?.comentario && String(pregunta.comentario).trim();
  const addPhotos =
    (pregunta?.havePhotos || pregunta?.additionalPhotos) &&
    Array.isArray(pregunta?.additionalPhotos) &&
    pregunta.additionalPhotos.length > 0;
  const addFiles =
    (pregunta?.haveFiles || pregunta?.additionalFiles) &&
    Array.isArray(pregunta?.additionalFiles) &&
    pregunta.additionalFiles.length > 0;
  return Boolean(hasCriticality || hasComentario || addPhotos || addFiles);
}

/** Criticidad, comentario, fotos/archivos adicionales (alineado con AgregadosSection de la app). */
function ProcedureAgregadosInline({
  pregunta,
  onImagePreview,
  compactTop = false,
}: {
  pregunta: any;
  onImagePreview: (src: string, alt?: string) => void;
  compactTop?: boolean;
}) {
  const hasCriticality = pregunta?.haveCriticality && pregunta?.criticality;
  const hasComentario =
    pregunta?.tieneComentario && pregunta?.comentario && String(pregunta.comentario).trim();
  const addPhotos =
    (pregunta?.havePhotos || pregunta?.additionalPhotos) &&
    Array.isArray(pregunta?.additionalPhotos) &&
    pregunta.additionalPhotos.length > 0;
  const addFiles =
    (pregunta?.haveFiles || pregunta?.additionalFiles) &&
    Array.isArray(pregunta?.additionalFiles) &&
    pregunta.additionalFiles.length > 0;

  if (!hasCriticality && !hasComentario && !addPhotos && !addFiles) {
    return null;
  }

  const criticalityLabel =
    hasCriticality && pregunta.criticality
      ? i18n(`entities.tarea.enumerators.prioridad.${pregunta.criticality}`)
      : '';

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        mt: compactTop ? 0 : 0.75,
        pt: compactTop ? 0 : 0.75,
        borderTop: compactTop ? 'none' : '1px solid',
        borderColor: 'divider',
        width: '100%',
        maxWidth: 280,
      }}>
      {hasCriticality && criticalityLabel && (
        <Box sx={{mb: 0.5}}>
          <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
            {i18n('common.criticalityLevel')}
          </Typography>
          <Typography variant="body2">{criticalityLabel}</Typography>
        </Box>
      )}
      {hasComentario && (
        <Box sx={{mb: 0.5}}>
          <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
            {i18n('common.additionalComments')}
          </Typography>
          <Typography variant="body2" sx={{wordBreak: 'break-word'}}>
            {String(pregunta.comentario).length > 120
              ? `${String(pregunta.comentario).slice(0, 120)}…`
              : pregunta.comentario}
          </Typography>
        </Box>
      )}
      {addPhotos && (
        <Box sx={{mb: 0.5}}>
          <Box display="flex" flexDirection="row" flexWrap="wrap" gap={0.5} mt={0.25}>
            {pregunta.additionalPhotos.map((raw: any, idx: number) => {
              const src = pickImagePreviewUrl(raw);
              const key = raw?.id ?? idx;
              return (
                <Box key={key}>
                  {src ? (
                    <Box
                      component="img"
                      src={src}
                      alt={raw?.name || ''}
                      onClick={() => onImagePreview(src, raw?.name)}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Pendiente
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      {addFiles && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
            {i18n('common.additionalDocuments')}
          </Typography>
          <Box display="flex" flexDirection="column" gap={0.25} mt={0.25}>
            {pregunta.additionalFiles.map((raw: any, idx: number) => {
              const url = raw?.publicUrl || raw?.downloadUrl;
              const name = raw?.name || 'archivo';
              const shortName = name.length > 14 ? `…${name.slice(-12)}` : name;
              return (
                <Box
                  key={raw?.id ?? idx}
                  component="button"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (url) {
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  sx={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    margin: 0,
                    textAlign: 'left',
                    cursor: url ? 'pointer' : 'default',
                  }}>
                  <Typography variant="body2" component="span" color={url ? 'primary' : 'text.secondary'}>
                    {shortName}
                  </Typography>
                  <Typography variant="caption" component="span" sx={{ml: 0.5, color: url ? 'success.main' : 'warning.main'}}>
                    {url ? '✓' : 'pendiente'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** Vista previa en fila: miniaturas + ImageModal; el resto sigue siendo texto. Clicks no abren el modal de fila. */
function DynamicTableCellPreview({pregunta}: {pregunta: any}) {
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const attachments = getImageAttachmentsForDynamicCell(pregunta);
  const [preview, setPreview] = useState<{src: string; alt?: string} | null>(null);
  const texto = getRespuestaTexto(pregunta, currentTenant);
  const textoTrim = String(texto ?? '').trim();
  const tieneAgregados = preguntaTieneAgregadosVisibles(pregunta);
  const sinRespuestaPrincipal =
    attachments.length === 0 && (textoTrim === '' || textoTrim === '-');
  const soloAgregados = sinRespuestaPrincipal && tieneAgregados;

  return (
    <>
      {!soloAgregados &&
        (attachments.length > 0 ? (
          <Box onClick={(e) => e.stopPropagation()} display="flex" flexDirection="row" flexWrap="wrap" gap={0.5} alignItems="center">
            {attachments.map((raw, idx) => {
              const src = pickImagePreviewUrl(raw);
              const key = raw?.id ?? idx;
              return (
                <Box key={key}>
                  {src ? (
                    <Box
                      component="img"
                      src={src}
                      alt={raw?.name || ''}
                      sx={{
                        width: 56,
                        height: 56,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'block',
                        verticalAlign: 'top',
                      }}
                      onClick={() => setPreview({src, alt: raw?.name})}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', maxWidth: 100}}>
                      Pendiente
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography variant="body2" component="span">
            {texto}
          </Typography>
        ))}
      <ProcedureAgregadosInline
        pregunta={pregunta}
        onImagePreview={(src, alt) => setPreview({src, alt})}
        compactTop={soloAgregados}
      />
      {preview ? <ImageModal src={preview.src} alt={preview.alt as any} onClose={() => setPreview(null)} /> : null}
    </>
  );
}

const PREGUNTA_UNREGISTER_KEYS = [
  'comentario',
  'criticality',
  'notApplicable',
  'files',
  'images',
  'textFromImage',
  'image',
  'additionalPhotos',
  'additionalFiles',
];

function collectPreguntaUnregisterPaths(basePath: string, pregunta: any): string[] {
  const paths = PREGUNTA_UNREGISTER_KEYS.map((key) => `${basePath}.${key}`);
  if (pregunta?.tipoRespuesta) {
    paths.push(`${basePath}.${pregunta.tipoRespuesta}`);
  }
  return paths;
}

function collectGrupoPreguntaUnregisterPaths(name: string, estructura: any[], startIndex: number): string[] {
  const paths: string[] = [];
  for (let i = startIndex; i < estructura.length; i++) {
    const entry = estructura[i];
    if (entry?.tipo === 'header') break;
    if (entry?.tipo !== 'grupo') continue;
    (entry.subsecciones || []).forEach((sub: any, subIdx: number) => {
      (sub.preguntas || []).forEach((pregunta: any, preguntaIdx: number) => {
        paths.push(
          ...collectPreguntaUnregisterPaths(`${name}.${i}.subsecciones.${subIdx}.preguntas.${preguntaIdx}`, pregunta),
        );
      });
    });
  }
  return paths;
}

function collectSubsectionPreguntaUnregisterPaths(name: string, grupoIdx: number, subsectionIdx: number, subseccion: any): string[] {
  const paths: string[] = [];
  (subseccion?.preguntas || []).forEach((pregunta: any, preguntaIdx: number) => {
    paths.push(
      ...collectPreguntaUnregisterPaths(
        `${name}.${grupoIdx}.subsecciones.${subsectionIdx}.preguntas.${preguntaIdx}`,
        pregunta,
      ),
    );
  });
  return paths;
}

function collectGrupoSubseccionesUnregisterPaths(name: string, grupoIdx: number, subsecciones: any[]): string[] {
  return (subsecciones || []).flatMap((sub, subIdx) =>
    collectSubsectionPreguntaUnregisterPaths(name, grupoIdx, subIdx, sub),
  );
}

function ContainerNotApplicableCheckbox({
  name,
  editar,
  onChange,
  inline,
}: {
  name: string;
  editar: boolean;
  onChange?: (checked: boolean) => void;
  inline?: boolean;
}) {
  return (
    <Box
      onClick={($e) => $e.stopPropagation()}
      sx={inline ? {flexShrink: 0} : {display: 'flex', justifyContent: 'center'}}>
      <CheckboxFormItem label={i18n('common.notApplicable')} name={name} disabled={!editar} onChange={onChange} />
    </Box>
  );
}

function clonarPreguntaParaNuevaFila(pregunta: any) {
  const flattenAlternativasCheckbox = (nodes = [], parentPath = '') => {
    const flat: any = [];
    nodes.forEach((node: any) => {
      const pathLabel = parentPath ? `${parentPath} > ${node.label}` : node.label;
      flat.push({id: node.id, label: pathLabel});
      if (Array.isArray(node.children) && node.children.length) {
        flat.push(...flattenAlternativasCheckbox(node.children, pathLabel));
      }
    });
    return flat;
  };
  const base: any = {
    ...pregunta,
    [pregunta.tipoRespuesta]: undefined,
    ...(pregunta.multiCheckboxOthers ? {multiCheckboxOthers: undefined} : {}),
    ...(pregunta.multipleChoiceOthers ? {multipleChoiceOthers: undefined} : {}),
    ...(pregunta.criticality ? {criticality: 'Media'} : {}),
    ...(pregunta.comentario ? {comentario: undefined} : {}),
    ...(pregunta.notApplicable ? {notApplicable: false} : {}),
  };

  if (pregunta.defaultFirstOption) {
    if (pregunta.tipoRespuesta === 'multipleChoice' && Array.isArray(pregunta.alternativas) && pregunta.alternativas.length > 0) {
      base[pregunta.tipoRespuesta] = alternativeSelectValue(pregunta.alternativas[pregunta.alternativas.length - 1]);
    }

    if (
      pregunta.tipoRespuesta === 'multiCheckbox' &&
      Array.isArray(pregunta.alternativasCheckbox) &&
      pregunta.alternativasCheckbox.length > 0
    ) {
      const alternativasCheckboxFlat = flattenAlternativasCheckbox(pregunta.alternativasCheckbox);
      base[pregunta.tipoRespuesta] = alternativasCheckboxFlat.map((a, idx) => ({
        id: a.id,
        label: a.label,
        value: idx === alternativasCheckboxFlat.length - 1,
      }));
    }
  }

  return base;
}

function isRespuestaVaciaByTipo(pregunta: any) {
  const tipoRespuesta = effectiveIncidentAnswerType(pregunta);
  if (!tipoRespuesta) return true;
  if (pregunta?.allowNotApplicable && pregunta?.notApplicable) return false;
  const respuesta =
    tipoRespuesta === 'yesNo'
      ? pregunta.yesNo ?? seedYesNoFromCheckbox(pregunta)
      : pregunta?.[tipoRespuesta];

  if (tipoRespuesta === 'multiCheckbox') {
    return !Array.isArray(respuesta) || !respuesta.some((item) => Boolean(item?.value));
  }
  if (tipoRespuesta === 'multipleChoice') {
    if (!respuesta) return true;
    if (respuesta === 'others') {
      return !pregunta?.multipleChoiceOthers;
    }
    return false;
  }
  if (tipoRespuesta === 'checkbox') {
    return respuesta === undefined || respuesta === null;
  }
  if (tipoRespuesta === 'files' || tipoRespuesta === 'images' || tipoRespuesta === 'textFromImage') {
    return !Array.isArray(respuesta) || respuesta.length === 0;
  }
  if (tipoRespuesta === 'signature') {
    return !pregunta?.signatureRef?.publicUrl && !pregunta?.signatureRef?.downloadUrl && !respuesta;
  }
  if (Array.isArray(respuesta)) {
    return respuesta.length === 0;
  }
  if (typeof respuesta === 'string') {
    return !respuesta.trim();
  }
  if (typeof respuesta === 'number') {
    return Number.isNaN(respuesta);
  }
  if (typeof respuesta === 'boolean') {
    return false;
  }
  if (respuesta && typeof respuesta === 'object') {
    return !respuesta.id && !respuesta.label;
  }
  return !respuesta;
}

function isFilaSinResponder(subseccion: any) {
  if (isContainerNotApplicable(subseccion)) return false;
  const preguntas = Array.isArray(subseccion?.preguntas) ? subseccion.preguntas : [];
  if (!preguntas.length) return true;
  return preguntas.every((pregunta) => isRespuestaVaciaByTipo(pregunta));
}

export default function TareaProcedimientoAnidadoForm(props) {
  const {name, esRespuesta, editar, customButton, record, formValidationErrors = {}} = props;
  const {control, setValue, unregister} = useFormContext();

  const estructuraAnidada = useWatch({control, name}) || [];
  const [modalData, setModalData] = useState<{grupoIdx: number; subsectionIndex: number} | null>(null);

  const initialTitle = useMemo(() => {
    const tipoFormulario: any = record?.tipoFormulario;
    const fechaLimite: any = record?.fechaLimite;
    const modeloTarea: any = record?.modeloTarea;
    const tituloTarea: any = record?.tituloTarea;
    const tituloOT: any = record?.titulo;
    const categoria: any = record?.categoria;
    if (fechaLimite) {
      // mantiene variable usada históricamente para no cambiar comportamiento de título
    }
    return tipoFormulario
      ? `${tipoFormulario} ${categoria?.nombre || ''}`
      : tituloOT
        ? `${tituloOT} ${tituloTarea || ''}`
        : modeloTarea?.titulo;
  }, [record]);

  if (!esRespuesta) return null;

  const addRow = (grupoIdx: number) => {
    const estructuraNueva = [...estructuraAnidada];
    const grupo = estructuraNueva[grupoIdx];
    if (!grupo?.subsecciones?.length) return;

    const subsecciones = [...grupo.subsecciones];
    if (subsecciones.length === 1 && isFilaSinResponder(subsecciones[0])) {
      setModalData({grupoIdx, subsectionIndex: 0});
      return;
    }
    const ultima = subsecciones[subsecciones.length - 1];
    const nuevoIndex = typeof ultima?.dynamicIndex === 'number' ? ultima.dynamicIndex + 1 : subsecciones.length;

    if (subsecciones.length > 0) {
      subsecciones[subsecciones.length - 1] = {
        ...ultima,
        isLastSubsection: false,
      };
    }

    const clon = {
      ...ultima,
      notApplicable: false,
      preguntas: (ultima?.preguntas || []).map((p) => clonarPreguntaParaNuevaFila(p)),
      subsectionIndex: nuevoIndex,
      dynamicIndex: nuevoIndex,
      isLastSubsection: true,
    };

    subsecciones.push(clon);
    estructuraNueva[grupoIdx] = {
      ...grupo,
      subsecciones,
    };
    setValue(name, estructuraNueva, {shouldDirty: true});
    setModalData({grupoIdx, subsectionIndex: subsecciones.length - 1});
  };

  const removeRow = () => {
    if (!modalData) return;
    const {grupoIdx, subsectionIndex} = modalData;
    const estructuraNueva = [...estructuraAnidada];
    const grupo = estructuraNueva[grupoIdx];
    if (!grupo?.subsecciones?.length || subsectionIndex <= 0) return;

    const subsecciones = [...grupo.subsecciones];
    const oldLength = subsecciones.length;
    subsecciones.splice(subsectionIndex, 1);
    const reindexadas = subsecciones.map((sub, idx) => ({
      ...sub,
      dynamicIndex: idx,
      subsectionIndex: idx,
      isLastSubsection: idx === subsecciones.length - 1,
    }));

    estructuraNueva[grupoIdx] = {
      ...grupo,
      subsecciones: reindexadas,
    };
    setValue(name, estructuraNueva, {shouldDirty: true});

    const orphanedSub = grupo.subsecciones[oldLength - 1];
    if (orphanedSub?.preguntas) {
      const fieldsToUnregister: string[] = [];
      orphanedSub.preguntas.forEach((pregunta, preguntaIdx) => {
        const basePath = `${name}.${grupoIdx}.subsecciones.${oldLength - 1}.preguntas.${preguntaIdx}`;
        fieldsToUnregister.push(`${basePath}.comentario`);
        if (pregunta.tipoRespuesta) {
          fieldsToUnregister.push(`${basePath}.${pregunta.tipoRespuesta}`);
        }
      });
      unregister(fieldsToUnregister);
    }

    setModalData(null);
  };

  const modalSubseccion =
    modalData && estructuraAnidada?.[modalData.grupoIdx]?.subsecciones?.[modalData.subsectionIndex]
      ? estructuraAnidada[modalData.grupoIdx].subsecciones[modalData.subsectionIndex]
      : null;

  return (
    <React.Fragment>
      <Grid
        style={{
          marginBottom: 20,
        }}
        item
        lg={12}
        md={12}
        sm={12}
        xs={12}>
        <Grid item id="container-form" xs={12} lg={12}>
          <Box>
            <Box
              display="flex"
              justifyContent="center"
              width="100%"
              style={{maxWidth: Boolean(customButton) ? '80%' : undefined}}>
              <Typography variant="h2">{initialTitle}</Typography>
            </Box>
            <Box style={{marginLeft: 'auto', alignSelf: 'center'}}>{customButton}</Box>
          </Box>
          <Divider sx={{mt: 1, mb: 2}} />

          {estructuraAnidada.map((entry, groupIndex) => {
            if (entry?.tipo === 'header') {
              return (
                <Grid
                  key={`header-${entry.sectionIndex}-${groupIndex}`}
                  item
                  lg={12}
                  sx={{borderBottom: '2px solid gray', mt: 1, mb: 1.5, py: 1}}>
                  <Grid item lg={12} sx={{px: 4}}>
                    <Box
                      display="grid"
                      gridTemplateColumns="1fr auto 1fr"
                      alignItems="center"
                      columnGap={1}>
                      <Box sx={{justifySelf: 'start'}}>
                        {entry.allowNotApplicable && (
                          <ContainerNotApplicableCheckbox
                            inline
                            name={`${name}.${groupIndex}.notApplicable`}
                            editar={editar}
                            onChange={(checked) => {
                              if (!checked) return;
                              const paths = collectGrupoPreguntaUnregisterPaths(name, estructuraAnidada, groupIndex + 1);
                              if (paths.length) unregister(paths, {keepValue: true} as any);
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="h3" fontSize="22px" textAlign="center">
                        {entry.categoria}
                      </Typography>
                      <Box />
                    </Box>
                  </Grid>
                </Grid>
              );
            }

            if (entry?.tipo !== 'grupo') return null;

            const headerEntry = [...estructuraAnidada]
              .slice(0, groupIndex)
              .reverse()
              .find((item: any) => item?.tipo === 'header');
            if (isContainerNotApplicable(headerEntry)) return null;

            const subsecciones = Array.isArray(entry.subsecciones) ? entry.subsecciones : [];
            const primeraSubseccion = subsecciones[0];
            const isDynamic = Boolean(primeraSubseccion?.isDynamic);
            const preguntasHeader = (primeraSubseccion?.preguntas || []).filter((p: any) => !p?.isTaskExecutorFromSource);

            if (!isDynamic) {
              return (
                <Box key={`grupo-estatico-${entry.sectionIndex}-${entry.listIndex}-${groupIndex}`} sx={{mb: 3}}>
                  {subsecciones.map((subseccion, subsectionIdx) => {
                    const subsectionNa = isContainerNotApplicable(subseccion);
                    return (
                    <Box key={`sub-estatica-${entry.sectionIndex}-${entry.listIndex}-${subsectionIdx}`} sx={{mb: 3}}>
                      {(subseccion.categoria || subseccion.allowNotApplicable) && (
                        <Box px={4}>
                          <Box
                            sx={{pt: 1, pb: 0}}
                            display="grid"
                            gridTemplateColumns="1fr auto 1fr"
                            alignItems="center"
                            columnGap={1}>
                            <Box sx={{justifySelf: 'start'}}>
                              {subseccion.allowNotApplicable && (
                                <ContainerNotApplicableCheckbox
                                  inline
                                  name={`${name}.${groupIndex}.subsecciones.${subsectionIdx}.notApplicable`}
                                  editar={editar}
                                  onChange={(checked) => {
                                    if (!checked) return;
                                    const paths = collectSubsectionPreguntaUnregisterPaths(
                                      name,
                                      groupIndex,
                                      subsectionIdx,
                                      subseccion,
                                    );
                                    if (paths.length) unregister(paths, {keepValue: true} as any);
                                  }}
                                />
                              )}
                            </Box>
                            {subseccion.categoria ? (
                              <Typography variant="h3" textAlign="center">
                                {subseccion.categoria}
                              </Typography>
                            ) : (
                              <Box />
                            )}
                            <Box />
                          </Box>
                          <Divider sx={{mt: 1.5, mb: 3}} />
                        </Box>
                      )}
                      {!subsectionNa &&
                      (subseccion.preguntas || []).map((pregunta, preguntaIdx) => {
                        const preguntaKey = `${name}.${groupIndex}.subsecciones.${subsectionIdx}.preguntas.${preguntaIdx}`;
                        const preguntaLabel = pregunta?.pregunta?.label || pregunta?.pregunta?.titulo;
                        const listIndex = entry.listIndex ?? 0;
                        const subsectionIndex = subseccion.subsectionIndex ?? subseccion.dynamicIndex ?? subsectionIdx;
                        const preguntaId = preguntaLabel ? `${preguntaLabel}__${listIndex}__${subsectionIndex}` : undefined;
                        const validationErrorMessage = preguntaId ? formValidationErrors[preguntaId] : undefined;
                        return (
                          <Box
                            px={4}
                            key={`preg-${entry.sectionIndex}-${subsectionIdx}-${preguntaIdx}`}
                            {...(preguntaId
                              ? {'data-pregunta-id': preguntaId}
                              : preguntaLabel
                                ? {'data-pregunta-label': preguntaLabel}
                                : {})}>
                            <RespuestaFormularioForm
                              secciones={true}
                              preguntaKey={preguntaKey}
                              item={pregunta}
                              editar={editar}
                              validationErrorMessage={validationErrorMessage}
                            />
                            <Divider sx={{mt: 2, mb: 3}} />
                          </Box>
                        );
                      })}
                    </Box>
                    );
                  })}
                </Box>
              );
            }

            const grupoNa = isContainerNotApplicable(primeraSubseccion);
            return (
              <Box key={`grupo-dinamico-${entry.sectionIndex}-${entry.listIndex}-${groupIndex}`} sx={{mb: 3}}>
                {(primeraSubseccion?.categoria || primeraSubseccion?.allowNotApplicable) && (
                  <Box px={4}>
                    <Box
                      sx={{pt: 1, pb: 0}}
                      display="grid"
                      gridTemplateColumns="1fr auto 1fr"
                      alignItems="center"
                      columnGap={1}>
                      <Box sx={{justifySelf: 'start'}}>
                        {primeraSubseccion?.allowNotApplicable && (
                          <ContainerNotApplicableCheckbox
                            inline
                            name={`${name}.${groupIndex}.subsecciones.0.notApplicable`}
                            editar={editar}
                            onChange={(checked) => {
                              subsecciones.forEach((_, subsectionIdx) => {
                                if (subsectionIdx === 0) return;
                                setValue(
                                  `${name}.${groupIndex}.subsecciones.${subsectionIdx}.notApplicable`,
                                  Boolean(checked),
                                  {shouldDirty: true},
                                );
                              });
                              if (!checked) return;
                              const paths = collectGrupoSubseccionesUnregisterPaths(name, groupIndex, subsecciones);
                              if (paths.length) unregister(paths, {keepValue: true} as any);
                            }}
                          />
                        )}
                      </Box>
                      {primeraSubseccion?.categoria ? (
                        <Typography variant="h3" textAlign="center">
                          {primeraSubseccion.categoria}
                        </Typography>
                      ) : (
                        <Box />
                      )}
                      <Box />
                    </Box>
                    <Divider sx={{mt: 1.5, mb: 3}} />
                  </Box>
                )}
                {!grupoNa && (
                  <Box px={4}>
                    <Paper
                      variant="outlined"
                      data-dynamic-table-index={groupIndex}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{fontWeight: 700, width: '80px'}}>N°</TableCell>
                              {preguntasHeader.map((pregunta, idx) => (
                                <TableCell key={`head-${entry.sectionIndex}-${entry.listIndex}-${idx}`} sx={{fontWeight: 700}}>
                                  {pregunta?.pregunta?.label || pregunta?.pregunta?.titulo || `Pregunta ${idx + 1}`}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {subsecciones.map((subseccion, subsectionIdx) => {
                              return (
                                <TableRow
                                  key={`row-${entry.sectionIndex}-${entry.listIndex}-${subsectionIdx}`}
                                  hover
                                  onClick={() => setModalData({grupoIdx: groupIndex, subsectionIndex: subsectionIdx})}>
                                  <TableCell>{(subseccion.dynamicIndex ?? subsectionIdx) + 1}</TableCell>
                                  {(subseccion.preguntas || [])
                                    .filter((p: any) => !p?.isTaskExecutorFromSource)
                                    .map((pregunta, preguntaIdx) => (
                                      <TableCell key={`cell-${entry.sectionIndex}-${subsectionIdx}-${preguntaIdx}`}>
                                        <DynamicTableCellPreview pregunta={pregunta} />
                                      </TableCell>
                                    ))}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {editar && (
                        <Box sx={{mt: 2, p: 2, pt: 0}}>
                          <Button variant="contained" startIcon={<Add />} onClick={() => addRow(groupIndex)}>
                            Agregar fila
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                )}
              </Box>
            );
          })}
        </Grid>
      </Grid>

      <Dialog
        fullWidth
        maxWidth="md"
        open={Boolean(modalData && modalSubseccion)}
        onClose={() => setModalData(null)}
        disableEnforceFocus
        aria-labelledby="modal-subseccion-dinamica-title">
        <DialogTitle id="modal-subseccion-dinamica-title">
          {modalSubseccion?.categoria || 'Responder fila'}
        </DialogTitle>
        <DialogContent dividers>
          {(modalSubseccion?.preguntas || []).map((pregunta, preguntaIdx) => {
            const preguntaKey = `${name}.${modalData?.grupoIdx}.subsecciones.${modalData?.subsectionIndex}.preguntas.${preguntaIdx}`;
            const preguntaLabel = pregunta?.pregunta?.label || pregunta?.pregunta?.titulo;
            const entry = estructuraAnidada?.[modalData?.grupoIdx ?? 0];
            const subseccion = entry?.subsecciones?.[modalData?.subsectionIndex ?? 0];
            const listIndex = entry?.listIndex ?? 0;
            const subsectionIndex = subseccion?.subsectionIndex ?? subseccion?.dynamicIndex ?? modalData?.subsectionIndex ?? 0;
            const preguntaId = preguntaLabel ? `${preguntaLabel}__${listIndex}__${subsectionIndex}` : undefined;
            const validationErrorMessage = preguntaId ? formValidationErrors[preguntaId] : undefined;
            return (
              <Box
                key={`modal-pregunta-${modalData?.grupoIdx}-${modalData?.subsectionIndex}-${preguntaIdx}`}
                sx={{mb: 3}}
                {...(preguntaId
                  ? {'data-pregunta-id': preguntaId}
                  : preguntaLabel
                    ? {'data-pregunta-label': preguntaLabel}
                    : {})}>
                <RespuestaFormularioForm
                  secciones={true}
                  preguntaKey={preguntaKey}
                  item={pregunta}
                  editar={editar}
                  validationErrorMessage={validationErrorMessage}
                />
                <Divider sx={{mt: 2}} />
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          {editar && (modalSubseccion?.dynamicIndex ?? 0) > 0 && (
            <Button color="error" onClick={removeRow}>
              Eliminar fila N°{(modalSubseccion?.dynamicIndex ?? 0) + 1}
            </Button>
          )}
          <Button onClick={() => setModalData(null)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
